/**
 * AIFinancialAdvisor — proactive insights widget.
 * Free: 3/mo, Pro: 30/mo, Premium: unlimited.
 */
import { useEffect, useState } from "react";
import { Sparkles, RefreshCw, AlertTriangle, TrendingUp, Wallet, PiggyBank, Landmark } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const CATEGORY_ICONS = {
  debt: Wallet,
  emergency: AlertTriangle,
  investing: TrendingUp,
  retirement: Landmark,
  tax: Landmark,
  career_finance: Sparkles,
  lifestyle: PiggyBank,
};

const URGENCY_TONE = {
  high: "border-destructive/40 bg-destructive/6",
  medium: "border-amber-500/40 bg-amber-500/6",
  low: "border-primary/40 bg-primary/6",
};

export default function AIFinancialAdvisor() {
  const { user } = useAuth();
  const tier = (user?.subscription_tier || "free").toLowerCase();
  const [insights, setInsights] = useState([]);
  const [quota, setQuota] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Load last historical insights on mount for a fast first paint
    api.get("/financial-plan/advisor/history?limit=1").then(({ data }) => {
      const last = (data.items || [])[0];
      if (last) {
        setInsights(last.insights || []);
      }
    }).catch(() => {});
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/financial-plan/advisor");
      setInsights(data.insights || []);
      setQuota(data.quota);
      toast.success(`Generated ${data.insights?.length || 0} insights`);
    } catch (e) {
      if (e.response?.status === 429) {
        const d = e.response.data?.detail;
        toast.error(d?.message || "Quota exhausted — upgrade for more.");
        setQuota({ tier: d?.tier, used: d?.used, limit: d?.quota });
      } else {
        toast.error("Failed to generate insights");
      }
    } finally { setBusy(false); }
  };

  const remaining = quota ? (quota.limit === -1 ? "∞" : Math.max(0, quota.limit - quota.used)) : null;

  return (
    <section className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/30 rounded-2xl p-6 mb-6" data-testid="ai-advisor-widget">
      <div className="flex items-center justify-between mb-4 gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-primary">
            <Sparkles size={12} /> AI financial advisor
          </div>
          <h2 className="font-display text-2xl font-bold mt-1 tracking-tight">
            {insights.length ? "Your financial picture, reasoned across." : "Get proactive AI insights"}
          </h2>
          <div className="text-xs text-muted-foreground mt-1">
            {insights.length
              ? "Refresh to re-analyze after changing any field below."
              : "Claude analyzes your entire plan — debts, income, retirement, life stage — and surfaces high-leverage moves."}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={generate}
            disabled={busy}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-60"
            data-testid="advisor-generate-btn"
          >
            <RefreshCw size={13} className={busy ? "animate-spin" : ""} />
            {busy ? "Analyzing…" : insights.length ? "Re-analyze" : "Generate insights"}
          </button>
          {quota && (
            <div className="text-[10px] text-muted-foreground font-mono">
              {tier}: {quota.used}/{quota.limit === -1 ? "∞" : quota.limit} used
              {remaining !== null && remaining !== "∞" && ` · ${remaining} left`}
            </div>
          )}
        </div>
      </div>

      {insights.length > 0 && (
        <div className="space-y-2">
          {(expanded ? insights : insights.slice(0, 3)).map((it, idx) => (
            <InsightCard key={idx} it={it} idx={idx} />
          ))}
          {insights.length > 3 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-primary hover:underline mt-2"
              data-testid="advisor-toggle-expand"
            >
              {expanded ? "Show less" : `Show ${insights.length - 3} more`}
            </button>
          )}
        </div>
      )}

      {!insights.length && !busy && tier === "free" && (
        <div className="mt-4 p-4 border border-dashed border-border rounded-lg text-xs text-muted-foreground flex items-center justify-between">
          <span>Free plan: 3 AI insights/month. Upgrade for proactive monitoring & 30/mo (Pro) or unlimited (Premium).</span>
          <Link to="/app/pricing" className="text-primary font-semibold whitespace-nowrap" data-testid="advisor-upgrade-link">Upgrade →</Link>
        </div>
      )}
    </section>
  );
}

function InsightCard({ it, idx }) {
  const Icon = CATEGORY_ICONS[it.category] || Sparkles;
  return (
    <div className={`border rounded-lg p-4 flex items-start gap-3 ${URGENCY_TONE[it.urgency] || URGENCY_TONE.low}`} data-testid={`insight-${idx}`}>
      <div className="w-8 h-8 rounded-md bg-card border border-border flex items-center justify-center flex-shrink-0">
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-sm">{it.title}</div>
          <span className="text-[9px] uppercase font-mono tracking-widest px-1.5 py-0.5 rounded bg-background/80 text-muted-foreground">{it.urgency}</span>
          {it.source && <span className="text-[9px] uppercase font-mono tracking-widest px-1.5 py-0.5 rounded bg-primary/10 text-primary">{it.source === "ai" ? "AI" : "auto"}</span>}
        </div>
        <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{it.body}</div>
        {it.expected_impact && <div className="text-[10px] mt-2 text-emerald-500 font-mono">↑ {it.expected_impact}</div>}
      </div>
    </div>
  );
}
